import React, { memo, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Avatar, Checkbox, IconButton } from "@toddle-edu/ds-web";
import { CaretClosedFilled, CaretOpenedFilled } from "@toddle-edu/ds-icons";
import classNames from "classnames";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import useSchools from "MyCalendar/hooks/useSchools";
import classes from "./SchoolHeader.scss";

const SchoolHeader = memo(
  ({
    schoolId,
    subtitle,
    isSubtitleLoading,
    isCollapsed,
    onCollapseToggle,
    viewOnly,
    isChecked,
    isIndeterminate,
    onCheck,
  }) => {
    const { schools } = useSchools();

    const school = useMemo(() => schools.find(s => s.id === schoolId) || {}, [
      schools,
      schoolId,
    ]);

    const schoolName = school.label || "";
    const schoolLogo = school.logo;

    const handleCollapseClick = useCallback(
      e => {
        e.stopPropagation();
        onCollapseToggle();
      },
      [onCollapseToggle]
    );

    const handleRowClick = useCallback(() => {
      if (!viewOnly && onCheck) onCheck();
    }, [viewOnly, onCheck]);

    const handleRowKeyDown = useCallback(
      e => {
        if (!viewOnly && onCheck) onEnterOrSpaceKeyDown(e, onCheck);
      },
      [viewOnly, onCheck]
    );

    return (
      <div
        className={classNames(classes.schoolHeader, {
          [classes.viewOnly]: viewOnly,
        })}
        onClick={handleRowClick}
        onKeyDown={handleRowKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className={classes.schoolHeaderLeft}>
          <Avatar
            dsVersion="2.0"
            src={schoolLogo}
            name={schoolName}
            shape="square"
            size="medium"
          />
          <div className={classes.schoolInfo}>
            <div className={classes.schoolNameRow}>
              <span className={`text-body ${classes.schoolName}`}>
                {schoolName}
              </span>
              {onCollapseToggle && (
                <IconButton
                  dsVersion="2.0"
                  variant="neutral"
                  type="plain"
                  size="x-small"
                  icon={
                    isCollapsed ? <CaretClosedFilled /> : <CaretOpenedFilled />
                  }
                  onClick={handleCollapseClick}
                />
              )}
            </div>
            {isSubtitleLoading ? (
              <SkeletonLoader width="160px" height="12px" />
            ) : subtitle ? (
              <span className={`text-body-s ${classes.schoolSubtitle}`}>
                {subtitle}
              </span>
            ) : null}
          </div>
        </div>
        {!viewOnly && (
          <Checkbox isChecked={isChecked} isIndeterminate={isIndeterminate} />
        )}
      </div>
    );
  }
);

SchoolHeader.displayName = "SchoolHeader";

SchoolHeader.propTypes = {
  schoolId: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  isSubtitleLoading: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  onCollapseToggle: PropTypes.func,
  viewOnly: PropTypes.bool,
  isChecked: PropTypes.bool,
  isIndeterminate: PropTypes.bool,
  onCheck: PropTypes.func,
};

SchoolHeader.defaultProps = {
  subtitle: undefined,
  isSubtitleLoading: false,
  isCollapsed: false,
  onCollapseToggle: undefined,
  viewOnly: false,
  isChecked: false,
  isIndeterminate: false,
  onCheck: undefined,
};

export default SchoolHeader;
